/**
 * One source per Dashboard tile (#2193 A). An admin's tiles read the blocks of
 * `summary/dashboard`, one request (`useDashboardSummary`); the route is
 * admin-only, so everyone else keeps the per-tile hooks. A source the user may
 * not open (no module) is idle: no request, no figure, no error.
 *
 * The badge counts (approvals, unread messages, absence requests, overdue
 * invoices, submitted time entries) are not here: the Dashboard reads the
 * sidebar's queries, which poll on every page anyway.
 */

import { useMemo } from "react";
import { addDays, format, parseISO } from "date-fns";
import { useAuthContext } from "@/contexts/AuthContext";
import { useDashboardSummary, type BlockSource, type DashboardBlockKey } from "@/hooks/useDashboardSummary";
import { expiringBfKeys } from "@/hooks/useExpiringBeitragsfaktoren";
import { useOrgCapacityOverview, useCapacityRange } from "@/hooks/useCapacity";
import {
  toLegacyCoverage,
  useCoverage,
  useLiveCoverage,
  useUnderstaffedGroups,
  type CoverageResponse,
} from "@/hooks/useShiftCoverage";
import { useShifts } from "@/hooks/useShifts";
import { useOpenGroupPresence } from "@/hooks/useGroupPresence";
import { useWorkflowStats, workflowCountsFromStats } from "@/hooks/useWorkflows";
import { useDebtorAccountsSummary } from "@/hooks/reconciliation/useDebtorAccountsList";
import { toUpcomingBirthdays, type UpcomingBirthdaysResponse } from "@/hooks/useBirthdays";
import { useSettlingInPlans } from "@/hooks/useSettlingIn";
import { SETTLING_IN_PROGRESS_PARAMS } from "@/lib/analytics/settlingInProgress";
import {
  toEventList,
  useChangesFeedSummary,
  useTodayActivityCount,
  useUpcomingEvents,
} from "@/hooks/useDashboard";
import { useCapacityAttention } from "@/hooks/useCapacityAttention";
import { daysAhead, UNDERSTAFFED_AHEAD_DAYS } from "@/lib/analytics/understaffedAhead";
import { OCCUPANCY_SPARK_DAYS } from "@/components/dashboard/useOccupancySparkline";
import { zurichDayStartUtc } from "@/components/cockpit/explorer/lib/analyticsPeriod";
import type { models_CalendarEvent } from "@/api/models/models_CalendarEvent";

/** The subsidy widget's window; the summary reads the same. */
export const SUBSIDY_WIDGET_DAYS = 90;

export interface DashboardAccess {
  capacity: boolean;
  shifts: boolean;
  workflows: boolean;
  billing: boolean;
  activities: boolean;
  settlingIn: boolean;
  calendar: boolean;
}

export interface DashboardSourcesOptions {
  /** The selected day, YYYY-MM-DD. */
  day: string;
  /** The selected day is today: the live staffing card shows. */
  isToday: boolean;
  /** The Overview tab is open: nothing is read on the Analytics tab. */
  isOverview: boolean;
  isAdmin: boolean;
  can: DashboardAccess;
  reconciliation: boolean;
  /** The location filter of the capacity attention list. */
  childcareId: string | null;
}

export interface WorkflowCounts {
  overdueCount: number;
  dueTodayCount: number;
}

const IDLE: BlockSource<never> = {
  data: undefined,
  error: null,
  isLoading: false,
  isError: false,
  isSuccess: false,
  isFetching: false,
  refetch: () => {},
};

/** The source, or idle when the user may not read it. */
function gate<T>(source: BlockSource<T>, allowed: boolean): BlockSource<T> {
  return allowed ? source : IDLE;
}

/** A source whose data is the block's, mapped (memoised by the caller). */
function mapped<A, B>(source: BlockSource<A>, map: (data: A) => B): BlockSource<B> {
  return { ...source, data: source.data === undefined ? undefined : map(source.data) };
}

/** A query result as a source. */
function fromQuery<T>(q: {
  data: T | undefined;
  error: unknown;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isFetching: boolean;
  refetch: () => unknown;
}): BlockSource<T> {
  return {
    data: q.data,
    error: q.error,
    isLoading: q.isLoading,
    isError: q.isError,
    isSuccess: q.isSuccess,
    isFetching: q.isFetching,
    refetch: () => void q.refetch(),
  };
}

/** Local (Europe/Zurich) midnight of day + offset, as an ISO instant. */
function localDayStart(day: string, offset: number): string {
  return zurichDayStartUtc(format(addDays(parseISO(day), offset), "yyyy-MM-dd")).toISOString();
}

function rfc3339Day(day: string, offset: number, end: boolean): string {
  return `${format(addDays(parseISO(day), offset), "yyyy-MM-dd")}${end ? "T23:59:59Z" : "T00:00:00Z"}`;
}

/** Today's shifts the "On shift now" row reads (mobile-management MANAGEMENT_ROSTER_LIMIT). */
const SHIFTS_LIMIT = 100;

export function useDashboardSources({
  day,
  isToday,
  isOverview,
  isAdmin,
  can,
  reconciliation,
  childcareId,
}: DashboardSourcesOptions) {
  const { orgId } = useAuthContext();
  const viaSummary = isAdmin;
  const direct = isOverview && !viaSummary;

  const seeds = useMemo(() => ({ bf_expiring: expiringBfKeys.list(orgId, SUBSIDY_WIDGET_DAYS) }), [orgId]);
  const skip = useMemo(() => {
    const out: DashboardBlockKey[] = [];
    if (!can.capacity) out.push("capacity", "occupancy_range", "capacity_attention");
    if (!can.shifts) out.push("coverage", "understaffed_ahead");
    if (!can.shifts || !isToday) out.push("live_coverage", "presence", "shifts");
    if (!can.workflows) out.push("workflow_stats");
    if (!can.billing || !reconciliation) out.push("debtor_summary");
    if (!can.settlingIn) out.push("settling_in");
    if (!can.calendar) out.push("events");
    if (!can.activities) out.push("activities");
    return out;
  }, [can.capacity, can.shifts, can.workflows, can.billing, can.settlingIn, can.calendar, can.activities, reconciliation, isToday]);
  const summary = useDashboardSummary({ asOf: day, enabled: isOverview && viaSummary, seeds, skip });
  const { block } = summary;

  // The per-tile reads, for a user without the summary.
  const sparkFrom = useMemo(() => format(addDays(parseISO(day), -(OCCUPANCY_SPARK_DAYS - 1)), "yyyy-MM-dd"), [day]);
  const ahead = useMemo(() => daysAhead(day, UNDERSTAFFED_AHEAD_DAYS), [day]);
  const capacityQ = useOrgCapacityOverview(day, true, { enabled: direct && can.capacity });
  const rangeQ = useCapacityRange(sparkFrom, day, "week", { enabled: direct && can.capacity });
  const coverageQ = useCoverage(day, { enabled: direct && can.shifts });
  const workflowQ = useWorkflowStats({ enabled: direct && can.workflows });
  const debtorQ = useDebtorAccountsSummary({ enabled: direct && can.billing && reconciliation });
  const settlingInQ = useSettlingInPlans(SETTLING_IN_PROGRESS_PARAMS, { enabled: direct && can.settlingIn });
  const changesQ = useChangesFeedSummary(30, { enabled: direct });
  const eventsQ = useUpcomingEvents(rfc3339Day(day, 0, false), rfc3339Day(day, 30, true), {
    enabled: direct && can.calendar,
  });
  // Local midnight to the next local midnight, the window of the management
  // app and of the summary block (cockpit/summary_surfaces.go): the same count.
  const activitiesQ = useTodayActivityCount(localDayStart(day, 0), localDayStart(day, 1), {
    enabled: direct && can.activities,
  });
  const understaffedQ = useUnderstaffedGroups(ahead.from, ahead.to, { enabled: direct && can.shifts });
  const live = isOverview && can.shifts && isToday;
  // A location filter narrows the live ratio: its own read, for everyone.
  const liveQ = useLiveCoverage({ childcareId, enabled: live && (!viaSummary || !!childcareId) });
  const presenceQ = useOpenGroupPresence({ enabled: live && !viaSummary });
  const shiftsQ = useShifts({ fromDate: day, toDate: day, limit: SHIFTS_LIMIT }, { enabled: live && !viaSummary });
  // A location filter narrows the list: its own read, for everyone.
  const attentionQ = useCapacityAttention({
    location: childcareId ?? undefined,
    enabled: isOverview && can.capacity && (!viaSummary || !!childcareId),
  });

  const coverageBlock = block("coverage");
  const workflowBlock = block("workflow_stats");
  const birthdaysBlock = block("birthdays");
  const eventsBlock = block("events");

  const coverage = useMemo<BlockSource<CoverageResponse>>(
    () => (viaSummary ? mapped(coverageBlock, (raw) => toLegacyCoverage(raw, day)) : fromQuery(coverageQ)),
    [viaSummary, coverageBlock, coverageQ, day],
  );
  const workflowStats = useMemo<BlockSource<WorkflowCounts>>(() => {
    if (viaSummary) return mapped(workflowBlock, workflowCountsFromStats);
    return {
      data: workflowQ.isLoading || workflowQ.isError ? undefined : workflowQ,
      error: workflowQ.error,
      isLoading: workflowQ.isLoading,
      isError: workflowQ.isError,
      isSuccess: !workflowQ.isLoading && !workflowQ.isError,
      isFetching: workflowQ.isLoading,
      refetch: () => void workflowQ.refetch(),
    };
  }, [viaSummary, workflowBlock, workflowQ]);
  const birthdays = useMemo<BlockSource<UpcomingBirthdaysResponse>>(
    () => mapped(birthdaysBlock, toUpcomingBirthdays),
    [birthdaysBlock],
  );
  const events = useMemo<BlockSource<models_CalendarEvent[]>>(
    () => (viaSummary ? mapped(eventsBlock, toEventList) : fromQuery(eventsQ)),
    [viaSummary, eventsBlock, eventsQ],
  );

  const pick = <T,>(fromSummary: BlockSource<T>, fallback: BlockSource<T>) => (viaSummary ? fromSummary : fallback);

  return {
    capacity: gate(pick(block("capacity"), fromQuery(capacityQ)), can.capacity),
    occupancyRange: gate(pick(block("occupancy_range"), fromQuery(rangeQ)), can.capacity),
    coverage: gate(coverage, can.shifts),
    workflowStats: gate(workflowStats, can.workflows),
    debtorSummary: gate(pick(block("debtor_summary"), fromQuery(debtorQ)), can.billing && reconciliation),
    // The Upcoming card shows birthdays to admins only.
    birthdays: gate(birthdays, viaSummary),
    settlingIn: gate(pick(block("settling_in"), fromQuery(settlingInQ)), can.settlingIn),
    changes: pick(block("changes"), fromQuery(changesQ)),
    events: gate(events, can.calendar),
    activities: gate(pick(block("activities"), fromQuery(activitiesQ)), can.activities),
    understaffedAhead: gate(pick(block("understaffed_ahead"), fromQuery(understaffedQ)), can.shifts),
    capacityAttention: gate(
      viaSummary && !childcareId ? block("capacity_attention") : fromQuery(attentionQ),
      can.capacity,
    ),
    liveCoverage: gate(viaSummary && !childcareId ? block("live_coverage") : fromQuery(liveQ), live),
    presence: gate(pick(block("presence"), fromQuery(presenceQ)), live),
    shiftsToday: gate(pick(block("shifts"), fromQuery(shiftsQ)), live),
    /** When the summary was last read (ms); 0 without the summary. */
    updatedAt: viaSummary ? summary.updatedAt : 0,
    /** The subsidy widget reads its own entry, which the summary fills: it waits for the summary. */
    subsidyReady: !viaSummary || summary.query.isSuccess || summary.query.isError,
    aheadRange: ahead,
  };
}
